import { CheckCircleIcon } from "@heroicons/react/24/outline";

import {
  IProductionManagerBay,
  IProductionManagerBayVehicle,
  ProductionManagerBayStatus,
} from "../../models/ProductionManager";
import classNames from "../../utils/classNames";
import { complexityChipClasses, complexityShort } from "../../utils/vehicleDisplay";

interface AssemblyFlowProps {
  bays: IProductionManagerBay[];
  assemblies: string[];
}

function VehicleChip({ vehicle }: { vehicle: IProductionManagerBayVehicle }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-2 py-1.5">
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-xs font-semibold text-gray-900 truncate">
          {vehicle.vrm || "No VRM"}
        </span>
        <span
          className={classNames(
            "text-[9px] font-semibold uppercase tracking-wide rounded px-1 py-0.5 shrink-0",
            complexityChipClasses(vehicle.complexity)
          )}
        >
          {complexityShort(vehicle.complexity)}
        </span>
      </div>
      <div className="text-[10px] text-gray-600 truncate">
        {[vehicle.combinedMake, vehicle.combinedModel].filter(Boolean).join(" ")}
      </div>
      <div className="mt-1 h-1 w-full rounded bg-gray-100 overflow-hidden">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${Math.min(100, vehicle.percentComplete)}%` }}
        />
      </div>
    </div>
  );
}

function BayFlowLane({ bay, assemblies }: { bay: IProductionManagerBay; assemblies: string[] }) {
  const vehicle = bay.currentVehicle;
  const isComplete = bay.status === ProductionManagerBayStatus.Complete;
  const activeAssembly = vehicle && !isComplete ? vehicle.currentAssemblyName : null;

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-800 truncate">{bay.name}</h3>
        {vehicle ? (
          <span className="text-[11px] text-gray-500 shrink-0">
            {isComplete ? "Complete" : `${vehicle.percentComplete}%`}
          </span>
        ) : (
          <span className="text-[11px] text-gray-400 shrink-0 italic">No vehicle</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-2 min-w-full">
          {assemblies.map((name, idx) => {
            const isActive = activeAssembly === name;
            return (
              <div
                key={name}
                className={classNames(
                  "flex-1 min-w-[140px] border rounded p-2",
                  isActive ? "bg-emerald-50 border-emerald-300" : "bg-gray-50 border-gray-200"
                )}
              >
                <div className="flex items-center justify-between gap-1 mb-1.5">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Assembly {idx + 1}
                    </div>
                    <div className="text-xs font-semibold text-gray-800 truncate">{name}</div>
                  </div>
                </div>
                {isActive && vehicle ? (
                  <VehicleChip vehicle={vehicle} />
                ) : (
                  <div className="text-[10px] text-gray-400 italic py-2 text-center">—</div>
                )}
              </div>
            );
          })}

          <div
            className={classNames(
              "flex-1 min-w-[140px] border rounded p-2",
              isComplete ? "bg-blue-50 border-blue-300" : "bg-blue-50/40 border-blue-100"
            )}
          >
            <div className="flex items-center gap-1 mb-1.5">
              <CheckCircleIcon
                className={classNames(
                  "h-3.5 w-3.5 shrink-0",
                  isComplete ? "text-blue-600" : "text-blue-300"
                )}
              />
              <div
                className={classNames(
                  "text-xs font-semibold",
                  isComplete ? "text-blue-800" : "text-blue-400"
                )}
              >
                Complete
              </div>
            </div>
            {isComplete && vehicle ? (
              <VehicleChip vehicle={vehicle} />
            ) : (
              <div className="text-[10px] text-blue-300 italic py-2 text-center">—</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AssemblyFlow({ bays, assemblies }: AssemblyFlowProps) {
  if (assemblies.length === 0 || bays.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {bays.map((bay) => (
        <BayFlowLane key={bay.id} bay={bay} assemblies={assemblies} />
      ))}
    </div>
  );
}
